/**
 * Evolución del tipo de cambio de un par: línea pequeña y su variación.
 *
 * La línea es escalonada a propósito: un tipo de cambio rige desde que se
 * publica (o se fija a mano) hasta el siguiente, no "viaja" entre uno y otro.
 * Los puntos manuales llevan marca propia, y el tooltip dice cuál es cuál:
 * un manual metido a media serie se lee distinto que un FIX oficial.
 */
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import type { FxSeriePunto } from "@/hooks/useFx";

const LINEA = "#3b82f6";
const MANUAL = "#d97706";
const DIA_MS = 86_400_000;

interface Punto {
  t: number;
  rate: number;
  manual: boolean;
  nota: string | null;
  /** El que se repite en "hoy" para que el escalón llegue al final. */
  vigente?: boolean;
}

export interface Variacion {
  actual: number | null;
  hace30: number | null;
  cambio: number | null;
  cambioPct: number | null;
  min: number | null;
  max: number | null;
}

/** Valor actual, cambio contra hace 30 días (en pesos y %), mínimo y máximo. */
export function variacion(puntos: FxSeriePunto[], ahora: number = Date.now()): Variacion {
  if (puntos.length === 0) {
    return { actual: null, hace30: null, cambio: null, cambioPct: null, min: null, max: null };
  }
  const actual = puntos[puntos.length - 1].rate;
  const corte = ahora - 30 * DIA_MS;
  const previos = puntos.filter((p) => new Date(p.effective_from).getTime() <= corte);
  const hace30 = previos.length ? previos[previos.length - 1].rate : null;
  const cambio = hace30 === null ? null : actual - hace30;
  const rates = puntos.map((p) => p.rate);
  return {
    actual,
    hace30,
    cambio,
    cambioPct: hace30 === null || cambio === null ? null : (cambio / hace30) * 100,
    min: Math.min(...rates),
    max: Math.max(...rates),
  };
}

const num = (n: number, d = 4) => n.toLocaleString("es-MX", { maximumFractionDigits: d });

function fecha(t: number): string {
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "America/Mexico_City",
  });
}

export function FxEvolucion({
  puntos,
  desde,
  par,
}: {
  puntos: FxSeriePunto[];
  desde: string;
  par: string;
}) {
  if (puntos.length === 0) return null;
  const ahora = Date.now();
  const inicio = new Date(desde).getTime();
  const datos: Punto[] = puntos.map((p) => ({
    // El que ya regía al abrir la ventana se pinta en su borde izquierdo.
    t: Math.max(new Date(p.effective_from).getTime(), inicio),
    rate: p.rate,
    manual: p.source === "manual",
    nota: p.notes,
  }));
  const ultimo = datos[datos.length - 1];
  datos.push({ ...ultimo, t: ahora, vigente: true, manual: false });

  const v = variacion(puntos, ahora);
  const hayManual = puntos.some((p) => p.source === "manual");

  return (
    <div className="mt-2" data-testid={`fx-evolucion-${par}`}>
      <div className="h-16" role="img" aria-label={`Evolución ${par}`}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={datos} margin={{ top: 6, right: 6, bottom: 2, left: 6 }}>
            <XAxis dataKey="t" type="number" domain={[inicio, ahora]} hide />
            <YAxis domain={["dataMin", "dataMax"]} hide />
            <Tooltip
              cursor={{ stroke: "#94a3b8", strokeWidth: 1 }}
              content={({ payload }) => {
                const p = payload?.[0]?.payload as Punto | undefined;
                if (!p) return null;
                return (
                  <div className="rounded border bg-background px-2 py-1 text-xs shadow">
                    <div className="font-medium tabular-nums">{num(p.rate)}</div>
                    <div className="text-muted-foreground">
                      {p.vigente
                        ? "vigente hoy"
                        : `${p.manual ? "Manual" : "FIX Banxico"} · ${fecha(p.t)}`}
                      {p.manual && p.nota ? ` · «${p.nota}»` : ""}
                    </div>
                  </div>
                );
              }}
            />
            <Line
              type="stepAfter"
              dataKey="rate"
              stroke={LINEA}
              strokeWidth={2}
              isAnimationActive={false}
              activeDot={{ r: 4, stroke: "var(--background, #fff)", strokeWidth: 2 }}
              dot={(props: { cx?: number; cy?: number; payload?: Punto; index?: number }) => {
                const { cx, cy, payload, index } = props;
                if (!payload?.manual || cx === undefined || cy === undefined) {
                  return <g key={`d${index}`} />;
                }
                return (
                  <circle
                    key={`d${index}`}
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill={MANUAL}
                    stroke="var(--background, #fff)"
                    strokeWidth={2}
                  />
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground tabular-nums">
        {v.cambio === null || v.cambioPct === null
          ? "Sin dato de hace 30 días"
          : `30 d: ${v.cambio >= 0 ? "+" : "−"}${num(Math.abs(v.cambio))} MXN (${
              v.cambioPct >= 0 ? "+" : "−"
            }${num(Math.abs(v.cambioPct), 2)} %)`}
        {v.min !== null && v.max !== null && ` · mín ${num(v.min)} · máx ${num(v.max)}`}
        {hayManual && (
          <span className="ml-2 inline-flex items-center gap-1">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: MANUAL }}
            />
            carga manual
          </span>
        )}
      </p>
    </div>
  );
}
